import { useThemeContext } from "@cohesive/ui-components";
import DOMPurify from "dompurify";
import { useEffect, useRef } from "react";

interface EmailBodyViewerProps {
  body: string;
}

// Detect if content is HTML
function isHtmlContent(content: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(content);
}

// Convert plain text to properly formatted HTML like Gmail does
// Gmail's approach: preserve line breaks, add minimal spacing
function plainTextToHtml(text: string): string {
  const lines = text.split(/\r?\n/);
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Empty line - add spacing between sections
    if (trimmed === "") {
      result.push('<div style="height: 0.75em;"></div>');
      continue;
    }

    // Check if this looks like a header (short all-caps line)
    const isHeader =
      trimmed === trimmed.toUpperCase() &&
      trimmed.length > 5 &&
      trimmed.length < 100 &&
      !/https?:/.test(trimmed) && // Not a URL
      /^[A-Z\s\d:,.\-()]+$/.test(trimmed);

    if (isHeader) {
      const escaped = escapeAndLinkify(trimmed);
      result.push(
        `<div style="font-weight: 600; margin: 0.75em 0 0.25em 0; font-size: 15px;">${escaped}</div>`,
      );
      continue;
    }

    // Check if it's a divider
    if (/^[-=_~]{3,}$/.test(trimmed)) {
      result.push(
        '<hr style="border: none; border-top: 1px solid currentColor; opacity: 0.15; margin: 1em 0;">',
      );
      continue;
    }

    // Regular line
    const escaped = escapeAndLinkify(line);
    result.push(`<div>${escaped}</div>`);
  }

  return result.join("\n");
}

// Escape HTML and linkify URLs and emails
function escapeAndLinkify(text: string): string {
  // Escape HTML
  let escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Convert URLs to links
  escaped = escaped.replace(
    /(https?:\/\/[^\s<]+)/gi,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
  );

  // Convert emails to mailto links
  escaped = escaped.replace(
    /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
    (email) => `<a href="mailto:${email}">${email}</a>`,
  );

  return escaped;
}

export function EmailBodyViewer({ body }: EmailBodyViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { mode } = useThemeContext();

  // Theme colors
  const isDark = mode === "dark";
  const backgroundColor = isDark ? "#0a0a0a" : "#ffffff";
  const textColor = isDark ? "#fafafa" : "#202124";
  const mutedColor = isDark ? "#a1a1aa" : "#5f6368";
  const linkColor = isDark ? "#60a5fa" : "#1a73e8";
  const borderColor = isDark ? "#27272a" : "#dadce0";
  const codeBackground = isDark ? "#18181b" : "#f1f3f4";

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    // Determine if HTML or plain text
    const isHtml = isHtmlContent(body);
    const processedContent = isHtml
      ? DOMPurify.sanitize(body, {
          ADD_TAGS: ["style"],
          ADD_ATTR: ["style", "class"],
        })
      : plainTextToHtml(body);

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <base target="_blank">
          <meta name="color-scheme" content="${isDark ? "dark" : "light"}">
          <meta name="supported-color-schemes" content="light dark">
          <style>
            :root {
              color-scheme: ${isDark ? "dark" : "light"};
            }
            
            /* Force dark mode overrides when in dark mode */
            ${
              isDark
                ? `
              .primary-text,
              [style*="color: #3c4043"],
              [style*="color:#3c4043"],
              div[class*="primary"] {
                color: #e8eaed !important;
              }
              
              .secondary-text,
              [style*="color: #70757a"],
              [style*="color:#70757a"] {
                color: #9aa0a6 !important;
              }
              
              .grey-button-text,
              [style*="color: #5f6368"],
              [style*="color:#5f6368"] {
                color: #bdc1c6 !important;
              }
              
              .accent-text,
              [style*="color: #1a73e8"],
              [style*="color:#1a73e8"] {
                color: #8ab4f8 !important;
              }
            `
                : ""
            }
          </style>
          <style>
            /* Base styles - only apply to non-styled elements */
            body {
              margin: 0;
              padding: 24px 16px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            
            /* Only style elements that don't have inline styles */
            div:not([style*="color"]) {
              line-height: 1.5;
            }
            
            p:not([style*="color"]) {
              margin: 0 0 0.5em 0;
              line-height: 1.5;
            }
            
            p:last-child {
              margin-bottom: 0;
            }
            
            /* Don't override link colors from email HTML */
            a:not([style*="color"]):not([class]) {
              color: ${linkColor};
              text-decoration: none;
            }
            
            a:hover {
              text-decoration: underline;
            }
            
            h1:not([style*="color"]), h2:not([style*="color"]), 
            h3:not([style*="color"]), h4:not([style*="color"]), 
            h5:not([style*="color"]), h6:not([style*="color"]) {
              margin: 1.5em 0 0.5em 0;
              line-height: 1.3;
              font-weight: 600;
            }
            
            h1:first-child, h2:first-child, h3:first-child {
              margin-top: 0;
            }
            
            ul, ol {
              margin: 1em 0;
              padding-left: 2em;
            }
            
            li {
              margin: 0.25em 0;
            }
            
            blockquote {
              margin: 1em 0;
              padding-left: 1em;
              border-left: 3px solid ${borderColor};
            }
            
            img {
              max-width: 100%;
              height: auto;
            }
            
            /* Tables - minimal styling */
            table {
              border-collapse: collapse;
            }
            
            table[align="center"] {
              margin-left: auto;
              margin-right: auto;
            }
            
            td, th {
              border: none;
            }
            
            /* Only add borders to data tables with border attribute */
            table[border]:not([border="0"]) td,
            table[border]:not([border="0"]) th {
              border: 1px solid ${borderColor};
              padding: 8px;
            }
            
            hr {
              border: none;
              border-top: 1px solid ${borderColor};
              margin: 1.5em 0;
            }
            
            code {
              background: ${codeBackground};
              padding: 2px 4px;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
              font-size: 0.9em;
            }
            
            pre {
              background: ${codeBackground};
              padding: 12px;
              border-radius: 4px;
              overflow-x: auto;
            }
            
            pre code {
              background: none;
              padding: 0;
            }
            
            .gmail_quote {
              margin: 1em 0;
              padding-left: 1em;
              border-left: 2px solid ${borderColor};
            }
          </style>
        </head>
        <body>${processedContent}</body>
      </html>
    `;

    doc.open();
    doc.write(html);
    doc.close();
  }, [
    body,
    backgroundColor,
    textColor,
    mutedColor,
    linkColor,
    borderColor,
    codeBackground,
  ]);

  return (
    <iframe
      ref={iframeRef}
      className="w-full h-full border-0"
      style={{ backgroundColor }}
      title="Email Content"
      sandbox="allow-same-origin allow-popups"
    />
  );
}
